// ────────────────────────────────────────────────────────────────────
// IntakeScreen — the customer's drop-zone + the operator's file room, as a
// three-pane file manager (Explorer-style, XZYNEXA-skinned) — "my computer,
// but better".
//
// FOLDERS are destinations: Inbox (received, unfiled) + one per asset that has
// documents. Panes: folders | files | preview.
//
// Interactions (staff): click / ⌘-click / shift-click multi-select · double-
// click to open · right-click menu · DRAG file(s) onto an asset folder to file ·
// bulk file-to-asset + bulk delete · Details ↔ Tiles · in-folder search ·
// batch upload (bounded concurrency, Stop, Retry) · filename → asset auto-guess.
// Customers browse + upload (read-only on filing/delete).
//
// This file is the SCREEN: state, queries, and the wiring between the panes.
// The panes themselves (components/intake/*), the two import wizards
// (screens/intake/*) and the pure predicates (lib/intakeFiles) live next door —
// it was 1,643 lines with all of that inlined, which is past the point where
// anyone reads it before changing it.
// ────────────────────────────────────────────────────────────────────

import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Inbox, Folder, FolderInput, UploadCloud, Trash2, Search, X, List, LayoutGrid,
         PanelRight, RotateCw, Building2, Layers, CalendarDays, File as FileIcon } from "lucide-react";
import { apiClient, getActiveTenant, getCachedUser, STAFF_ROLES } from "@/lib/api";
import { ImportConsole } from "@/components/intake/ImportConsole";
import { classifyColumns, type Classification } from "@/lib/importClassify";
import { FileTabs } from "@/components/files/FileTabs";
import { MAX_MB, UPLOAD_CONCURRENCY, runLimited, typeLabel, deliveryTitle, openFile,
         TABULAR, canImportFile, canRegisterImportFile, KIND_LABEL, KIND_ORDER,
         type Location, type SortKey, type Menu, type UploadItem } from "@/lib/intakeFiles";
import { KindIcon, FolderRow, TreeHeader } from "@/components/intake/primitives";
import { DetailsView, TilesView, PreviewPane, ContextMenu } from "@/components/intake/FileViews";
import { AssetPickerModal } from "@/components/intake/AssetPickerModal";
import { UploadPanel } from "@/components/intake/UploadPanel";
import { DocumentFilters, emptyReason, type FilterValue, type DocumentFacets } from "@/components/intake/DocumentFilters";
import { ImportModal } from "@/screens/intake/ImportModal";
import { RegisterImportModal } from "@/screens/intake/RegisterImportModal";
import type { IntakeFile } from "@/lib/types";


export default function IntakeScreen({ lens = "queue" }: { lens?: "queue" | "assets" }): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isStaff = (STAFF_ROLES as readonly string[]).includes(getCachedUser()?.role ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [dragIds, setDragIds] = useState<number[]>([]);
  const [dropFolder, setDropFolder] = useState<number | "inbox" | null>(null);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [uploadOpen, setUploadOpen] = useState(true);
  const stopRef = useRef(false);                                  // whole-batch stop
  const ctrls = useRef<Map<string, AbortController>>(new Map());  // per-file abort
  const cancelled = useRef<Set<string>>(new Set());               // per-file cancel flags
  const [loc, setLoc] = useState<Location>({ kind: "inbox" });
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [anchor, setAnchor] = useState<number | null>(null);
  const [menu, setMenu] = useState<Menu>(null);
  const [pickTargets, setPickTargets] = useState<IntakeFile[] | null>(null);
  const [toast, setToast] = useState<{ msg: string; undo?: () => void } | null>(null);
  const [importing, setImporting] = useState<IntakeFile | null>(null);
  const [registerImporting, setRegisterImporting] = useState<IntakeFile | null>(null);
  // Which lens the route asked for. This used to be `mode` state; it lives in
  // the path now so a tab can be bookmarked (see FileTabs). Named `lens`, not
  // `view` — `view` is already taken below for details-vs-tiles.
  const mode = lens === "assets" ? "room" : "console";
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());   // collapsed plant/area keys in the tree
  const toggleCollapse = (key: string) => setCollapsed((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({ key: "date", asc: false });
  const [view, setView] = useState<"details" | "tiles">("details");
  const [filter, setFilter] = useState("");
  const [showPreview, setShowPreview] = useState(true);

  const [params, setParams] = useSearchParams();
  const location = useLocation();
  // Captured once, on the render this component was first mounted with — NOT
  // re-derived on every render — because the deep-link effect below strips
  // `?file=` from the URL once it resolves. If this were computed fresh each
  // time, the queue lens would flip back to the Import Console (see `mode`
  // below) the instant the parameter was removed, right as the document was
  // about to be shown. A staff user arriving at /files/queue?file={id} —
  // exactly what a "loose", unfiled search hit deep-links to — must see the
  // file room, not the console, for the whole visit.
  const [cameFromFileDeepLink] = useState(() => params.get("file") != null);
  const [deepLinkMiss, setDeepLinkMiss] = useState(false);
  // A loc change normally means the user clicked a different folder, which
  // clears the selection (see the `[loc]` effect below). The deep-link effect
  // also changes loc (to the folder that holds the linked file) and needs its
  // OWN selection to survive that — this ref tells the reset effect to skip
  // itself exactly once.
  const suppressLocResetRef = useRef(false);
  // Which deep-linked file id we have already re-fetched superseded revisions
  // for. Search CAN return a superseded revision (routers/search.py filters on
  // deleted_at only — deliberately, since matching on `revision` is how an old
  // one gets found), but the default list cannot see one. Widening exactly once
  // per id keeps that from becoming a fetch loop.
  const widenedForRef = useRef<number | null>(null);

  // ── No tenant bound: the one check that came here from the retired "/" ──
  //
  // DeskHomeScreen was a greeting and one card whose destination was this
  // screen, and it is deleted (2026-09-01, spec: TODAY is the queue). The card
  // needed no new home — FILES already IS what it opened — but ONE thing on it
  // did, and it was not decoration: a staff user with no tenant selected must
  // not be invited to import. X-Tenant-Id comes from getActiveTenant(); with
  // nothing chosen the upload resolves to the DEFAULT sentinel and lands
  // somewhere the reader never picked. That is silent data loss.
  //
  // The desk covered only one of the two doors into here — the nav always led
  // straight past it — so this closes a hole rather than merely preserving one.
  // It costs no round trip: getActiveTenant() is a localStorage read.
  //
  // Customers are exempt for the same reason as before: resolve_tenant_for_user
  // falls back to their first membership server-side, so a portal user always
  // has somewhere real to point.
  //
  // Scoped to the CONSOLE, not to the whole screen, and deliberately: the card
  // guarded an invitation to WRITE. The file room below reads — trash, per-asset
  // documents, revisions — and blocking a read would be a new restriction
  // wearing a moved check's clothes. Someone who cleared their tenant on
  // purpose keeps browsing exactly as they did yesterday.
  const noTenant = isStaff && mode === "console" && !cameFromFileDeepLink && !getActiveTenant();
  const listQ = useQuery({ queryKey: ["intake"], queryFn: () => apiClient.intakeList(), staleTime: 20_000, enabled: !noTenant });
  const all: IntakeFile[] = listQ.data ?? [];
  const trashQ = useQuery({ queryKey: ["intake", "trash"], queryFn: () => apiClient.intakeList({ status: "trash" }), staleTime: 20_000, enabled: !noTenant });
  const trash: IntakeFile[] = trashQ.data ?? [];
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["intake"] });
    qc.invalidateQueries({ queryKey: ["intake-facets"] });
  };

  // The document filter row (above the file table, file-room view only) and
  // the facet counts that populate its dropdowns. Facets are fetched
  // separately, not derived from `all`, so a filter row can show "12 report"
  // before the (possibly much larger) unfiltered list has finished loading —
  // and per the plan, must not block the row from rendering: empty facets
  // ({years:[],undated:0,kinds:{}}) render a usable, if sparse, row rather
  // than making it appear late and shove the table down.
  const [filters, setFilters] = useState<FilterValue>({ currentOnly: true });
  const facetsQ = useQuery({
    queryKey: ["intake-facets"],
    queryFn: apiClient.intakeFacets,
    staleTime: 60_000,
    enabled: !noTenant,
  });
  const facets: DocumentFacets = facetsQ.data ?? { years: [], undated: 0, kinds: {} };
  // `all` above is fetched with the server default current_only=true, so the
  // superseded rows it excludes were never sent to the browser — flipping the
  // "รวมฉบับที่ถูกแทนที่" chip needs its own fetch, not a client-side filter.
  // Only requested when the toggle is actually off.
  const supersededQ = useQuery({
    queryKey: ["intake", "with-superseded"],
    queryFn: () => apiClient.intakeList({ current_only: false }),
    staleTime: 20_000,
    enabled: !filters.currentOnly && !noTenant,
  });
  const basePool: IntakeFile[] = filters.currentOnly ? all : (supersededQ.data ?? all);
  // True while the pool a deep link resolves against is still arriving. Without
  // the second half, flipping currentOnly off (to reach a superseded revision)
  // would be judged a miss on the very next render — before its fetch returns.
  const poolLoading = listQ.isFetching
    || (!filters.currentOnly && (supersededQ.isPending || supersededQ.isFetching));

  const inbox = useMemo(() => all.filter((r) => r.status === "received"), [all]);

  // What each waiting spreadsheet IS, read from its own column headers, so the
  // console can state it instead of asking a question it can already answer.
  // Only for staff on the console (a customer never sees this), only for
  // tabular files (a PDF is a document by definition), and cached by
  // react-query — /inspect downloads and parses the file server-side, so one
  // call per file, not one per render.
  const tabularPending = useMemo(
    () => (isStaff ? inbox.filter((f) => f.kind === "data" || TABULAR.test(f.filename)) : []),
    [isStaff, inbox]);
  const inspections = useQueries({
    queries: tabularPending.map((f) => ({
      queryKey: ["intake-inspect", f.id],
      queryFn: () => apiClient.intakeInspect(f.id),
      staleTime: 5 * 60_000,
      retry: false,          // an unreadable sheet is a verdict, not a retry
    })),
  });
  const classified = useMemo(() => {
    const out: Record<number, Classification | undefined> = {};
    tabularPending.forEach((f, i) => {
      const q = inspections[i];
      if (!q || q.isPending) return;                       // still reading
      out[f.id] = q.data ? classifyColumns(q.data.columns ?? []) : { kind: "unknown", matched: [] };
    });
    return out;
  }, [tabularPending, inspections]);

  const typeCounts = useMemo(
    () => KIND_ORDER.map((t) => ({ t, n: all.filter((r) => r.kind === t).length })).filter((x) => x.n > 0),
    [all]);

  // Filed documents grouped into the real hierarchy: Plant → Area → Asset.
  const assetTree = useMemo(() => {
    type A = { id: number; tag: string; count: number };
    type Ar = { key: string; name: string; assets: Map<number, A> };
    type P = { key: string; id: number | null; name: string; areas: Map<string, Ar>; count: number };
    const plants = new Map<string, P>();
    for (const r of all) {
      if (r.status !== "filed" || !r.asset_id) continue;
      const pkey = r.plant_id != null ? `p${r.plant_id}` : "p0";
      let p = plants.get(pkey);
      if (!p) { p = { key: pkey, id: r.plant_id, name: r.plant_name ?? "Unassigned", areas: new Map(), count: 0 }; plants.set(pkey, p); }
      p.count += 1;
      const akey = r.area_name ?? "—";
      let a = p.areas.get(akey);
      if (!a) { a = { key: `${pkey}:${akey}`, name: akey, assets: new Map() }; p.areas.set(akey, a); }
      let as = a.assets.get(r.asset_id);
      if (!as) { as = { id: r.asset_id, tag: r.asset_tag ?? `#${r.asset_id}`, count: 0 }; a.assets.set(r.asset_id, as); }
      as.count += 1;
    }
    return [...plants.values()].sort((x, y) => x.name.localeCompare(y.name)).map((p) => ({
      key: p.key, name: p.name, count: p.count,
      areas: [...p.areas.values()].sort((x, y) => x.name.localeCompare(y.name)).map((a) => ({
        key: a.key, name: a.name,
        assets: [...a.assets.values()].sort((x, y) => x.tag.localeCompare(y.tag)),
      })),
    }));
  }, [all]);

  // Everything the open folder and the filter row narrow to, EXCEPT the year
  // filter. Split out because this — not the tenant — is the population a year
  // filter selects from, so it is also the population whose undated rows that
  // filter hides. §6.3's sentence is about the documents the reader is standing
  // in front of; facets.undated is the whole tenant's backlog and would answer
  // 412 while the honest answer for this folder is 0.
  const scoped = useMemo(() => {
    let files: IntakeFile[];
    switch (loc.kind) {
      case "inbox": files = inbox; break;
      case "all":   files = basePool; break;
      case "trash": files = trash; break;
      case "type":  files = basePool.filter((r) => r.kind === loc.t); break;
      default:      files = basePool.filter((r) => r.asset_id === loc.id && r.status === "filed");
    }
    // The filter row (kind/direction) narrows whatever folder is open — it does
    // not replace the folder navigation above it.
    if (filters.kind) files = files.filter((r) => r.kind === filters.kind);
    if (filters.direction) files = files.filter((r) => r.direction === filters.direction);
    if (filter.trim()) {
      const q = filter.trim().toLowerCase();
      files = files.filter((r) => r.filename.toLowerCase().includes(q) || (r.note ?? "").toLowerCase().includes(q));
    }
    return files;
  }, [loc, inbox, basePool, trash, filter, filters.kind, filters.direction]);

  const undatedInScope = useMemo(
    () => scoped.filter((r) => !r.document_date).length, [scoped]);

  const current = useMemo(() => {
    let files = scoped;
    if (filters.year === "none") files = files.filter((r) => !r.document_date);
    else if (filters.year) files = files.filter((r) => (r.document_date ?? "").slice(0, 4) === filters.year);
    const dir = sort.asc ? 1 : -1;
    return [...files].sort((a, b) => {
      switch (sort.key) {
        case "name": return a.filename.localeCompare(b.filename) * dir;
        case "type": return typeLabel(a).localeCompare(typeLabel(b)) * dir;
        case "size": return (a.size_bytes - b.size_bytes) * dir;
        default:     return ((a.filed_at ?? a.uploaded_at) < (b.filed_at ?? b.uploaded_at) ? -1 : 1) * dir;
      }
    });
  }, [scoped, filters.year, sort]);

  const inTrash = loc.kind === "trash";
  // Must match what `current` (the table) actually renders from — basePool,
  // not `all` — or a selected row that only exists because "รวมฉบับที่ถูกแทนที่"
  // is on (a superseded revision) is visible/selectable in the table but
  // silently missing from selectedFiles: the preview drawer goes blank and
  // bulk actions (file-to-asset, context menu) drop it with no error.
  const pool = inTrash ? trash : basePool;
  const selectedFiles = useMemo(() => pool.filter((r) => sel.has(r.id)), [pool, sel]);
  const previewFile = sel.size === 1 ? selectedFiles[0] : null;

  const route = useMutation({ mutationFn: (v: { id: number; asset_id: number | null }) => apiClient.intakeFileToAsset(v.id, { asset_id: v.asset_id }),
    onSuccess: refresh });
  const patchMut = useMutation({ mutationFn: (v: { id: number; body: { filename?: string; note?: string; document_date?: string | null } }) => apiClient.intakePatch(v.id, v.body),
    onSuccess: refresh });

  // Soft-delete N files, then offer Undo (restore) via a toast.
  const trashFiles = async (ids: number[]) => {
    await runLimited(ids, 4, async (id) => { try { await apiClient.intakeDelete(id); } catch { /* skip */ } });
    setSel(new Set()); refresh();
    setToast({ msg: ids.length > 1 ? `${ids.length} files moved to Trash` : "Moved to Trash", undo: async () => {
      await runLimited(ids, 4, async (id) => { try { await apiClient.intakeRestore(id); } catch { /* skip */ } });
      refresh(); setToast(null);
    } });
  };
  // Staff received files outside the portal (LINE/email), uploaded them here,
  // and records them as the customer's delivery — one click, no wizard.
  const recordAsDelivery = async (ids: number[]) => {
    if (!confirm(`บันทึก ${ids.length} ไฟล์เป็นรอบส่งของลูกค้า?`)) return;
    const names = ids.map((id) => pool.find((r) => r.id === id)?.filename ?? "").filter(Boolean);
    try {
      await apiClient.submissionCreate({
        title: deliveryTitle(names, " (ทีมรับแทน)"),
        file_ids: ids, submit: true,
      });
    } catch {
      setToast({ msg: "บันทึกรอบส่งไม่สำเร็จ" });
      return;
    }
    setSel(new Set()); refresh();
    qc.invalidateQueries({ queryKey: ["submissions"] });
    setToast({ msg: `บันทึกรอบส่ง ${ids.length} ไฟล์แล้ว` });
  };
  const restoreFiles = async (ids: number[]) => {
    await runLimited(ids, 4, async (id) => { try { await apiClient.intakeRestore(id); } catch { /* skip */ } });
    setSel(new Set()); refresh();
  };
  const purgeFiles = async (ids: number[]) => {
    if (!confirm(`Permanently delete ${ids.length} file(s)? This cannot be undone.`)) return;
    await runLimited(ids, 4, async (id) => { try { await apiClient.intakePurge(id); } catch { /* skip */ } });
    setSel(new Set()); refresh();
  };
  const renameOne = (f: IntakeFile) => {
    const name = window.prompt("Rename file", f.filename);
    if (name && name.trim() && name.trim() !== f.filename) patchMut.mutate({ id: f.id, body: { filename: name.trim() } });
  };
  const editNote = (f: IntakeFile) => {
    const note = window.prompt("Note / label", f.note ?? "");
    if (note !== null && note !== f.note) patchMut.mutate({ id: f.id, body: { note } });
  };

  // ── The date printed ON the document (§4.4) ──────────────────────────────
  // Two ways in, because a column that ships with neither stays NULL forever
  // and every filter built on it is decoration: one at filing (the preview
  // pane's field, below) and one over a selection (historical documents arrive
  // as "the 2019 pile").
  //
  // NEVER inferred. The prompt is seeded with the value already stored, or with
  // nothing at all — not with a year scraped off the filename, not with
  // uploaded_at. A wrong date in an archive is worse than a missing one, and
  // this codebase has already shipped one filename-guessing bug (#147).
  const DATE_HINT = "วันที่บนเอกสาร (YYYY-MM-DD) — เว้นว่างเพื่อล้าง";
  const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
  /** null = the user cancelled; "" = clear it; otherwise a validated ISO date. */
  const askForDate = (seed: string): string | null => {
    const raw = window.prompt(DATE_HINT, seed);
    if (raw === null) return null;
    const v = raw.trim();
    if (v && !ISO_DATE.test(v)) {
      setToast({ msg: "วันที่ต้องเป็นรูปแบบ YYYY-MM-DD" });
      return null;
    }
    return v;
  };
  const editDate = (f: IntakeFile) => {
    const v = askForDate(f.document_date ?? "");
    if (v === null) return;
    const next = v === "" ? null : v;
    if (next === (f.document_date ?? null)) return;
    patchMut.mutate({ id: f.id, body: { document_date: next } });
  };
  const setDatesFor = async (ids: number[]) => {
    if (!ids.length) return;
    const v = askForDate("");
    if (v === null) return;
    try {
      const res = await apiClient.intakeSetDates({ file_ids: ids, document_date: v === "" ? null : v });
      refresh();
      setToast({ msg: v === "" ? `ล้างวันที่ ${res.updated} ฉบับ` : `ระบุวันที่ ${v} ให้ ${res.updated} ฉบับ` });
    } catch {
      setToast({ msg: "ระบุวันที่ไม่สำเร็จ" });
    }
  };

  const patchUpload = useCallback((key: string, patch: Partial<UploadItem>) => {
    setUploads((prev) => prev.map((u) => (u.key === key ? { ...u, ...patch } : u)));
  }, []);

  // Upload one item under its own AbortController (so it can be cancelled alone).
  const sendOne = useCallback(async (it: UploadItem) => {
    if (stopRef.current || cancelled.current.has(it.key)) { patchUpload(it.key, { status: "stopped", note: "stopped" }); return; }
    const ctrl = new AbortController(); ctrls.current.set(it.key, ctrl);
    patchUpload(it.key, { status: "uploading", pct: 0, note: undefined });
    try {
      await apiClient.intakeUpload(it.file, "", (pct) => patchUpload(it.key, { pct }), ctrl.signal);
      patchUpload(it.key, { status: "done", pct: 100 });
      qc.invalidateQueries({ queryKey: ["intake"] });
    } catch {
      const stopped = stopRef.current || cancelled.current.has(it.key);
      patchUpload(it.key, { status: stopped ? "stopped" : "failed", note: stopped ? "stopped" : "upload failed" });
    } finally { ctrls.current.delete(it.key); }
  }, [patchUpload, qc]);

  const uploadFiles = useCallback(async (files: File[]) => {
    // Duplicate warning: same filename + size already present (non-deleted).
    const existing = new Set(all.map((r) => `${r.filename} ${r.size_bytes}`));
    const dupes = files.filter((f) => existing.has(`${f.name} ${f.size}`));
    if (dupes.length && !confirm(`${dupes.length} file(s) look already uploaded (same name & size): ${dupes.slice(0, 3).map((f) => f.name).join(", ")}${dupes.length > 3 ? "…" : ""}\n\nUpload anyway?`)) {
      files = files.filter((f) => !existing.has(`${f.name} ${f.size}`));
      if (!files.length) return;
    }
    stopRef.current = false;
    const stamp = Date.now();
    const items: UploadItem[] = files.map((f, i) => {
      const bad = f.size > MAX_MB * 1024 * 1024 ? `> ${MAX_MB} MB` : f.size === 0 ? "empty file" : null;
      return { key: `${stamp}-${i}-${f.name}`, name: f.name, size: f.size, pct: 0, file: f,
               status: bad ? "failed" : "queued", note: bad ?? undefined };
    });
    setUploads((prev) => [...items, ...prev]);           // newest batch on top
    setUploadOpen(true);
    const queued = items.filter((i) => i.status === "queued");
    await runLimited(queued, UPLOAD_CONCURRENCY, sendOne);
    setLoc({ kind: "inbox" });
  }, [sendOne, all]);

  const retryOne = useCallback((key: string) => {
    const it = uploads.find((u) => u.key === key);
    if (!it || it.size === 0 || it.size > MAX_MB * 1024 * 1024) return;   // permanent rejects
    cancelled.current.delete(key);
    stopRef.current = false;                             // a manual retry overrides a prior batch Stop
    void sendOne({ ...it, status: "queued", pct: 0, note: undefined });
  }, [uploads, sendOne]);

  const cancelOne = useCallback((key: string) => {
    cancelled.current.add(key);
    const c = ctrls.current.get(key);
    if (c) c.abort();                                    // in-flight → catch marks stopped
    else patchUpload(key, { status: "stopped", note: "stopped" });   // still queued
  }, [patchUpload]);

  const stopUploads = () => { stopRef.current = true; ctrls.current.forEach((c) => c.abort()); };
  const retryAll = () => uploads
    .filter((u) => (u.status === "failed" || u.status === "stopped") && u.size > 0 && u.size <= MAX_MB * 1024 * 1024)
    .forEach((u) => retryOne(u.key));

  const isFileDrag = (e: React.DragEvent) => e.dataTransfer.types.includes("Files");
  const onPaneDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    if (isFileDrag(e)) { const files = Array.from(e.dataTransfer.files); if (files.length) void uploadFiles(files); }
  }, [uploadFiles]);

  const dropOnFolder = async (target: number | "inbox") => {
    const ids = dragIds; setDragIds([]); setDropFolder(null);
    if (!ids.length) return;
    await runLimited(ids, 4, async (id) => { try { await apiClient.intakeFileToAsset(id, { asset_id: target === "inbox" ? null : target }); } catch { /* skip */ } });
    setSel(new Set()); qc.invalidateQueries({ queryKey: ["intake"] });
  };


  // selection handlers (shared by both views)
  const rowClick = (e: React.MouseEvent, r: IntakeFile) => {
    e.stopPropagation();
    if (e.shiftKey && anchor != null) {
      const ids = current.map((f) => f.id);
      const a = ids.indexOf(anchor), b = ids.indexOf(r.id);
      if (a >= 0 && b >= 0) { const [lo, hi] = a < b ? [a, b] : [b, a]; setSel(new Set(ids.slice(lo, hi + 1))); }
    } else if (e.metaKey || e.ctrlKey) {
      setSel((prev) => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; }); setAnchor(r.id);
    } else { setSel(new Set([r.id])); setAnchor(r.id); }
  };
  const rowMenu = (e: React.MouseEvent, r: IntakeFile) => {
    e.preventDefault();
    if (!sel.has(r.id)) { setSel(new Set([r.id])); setAnchor(r.id); }
    setMenu({ x: e.clientX, y: e.clientY, file: r });
  };
  const rowDragStart = (r: IntakeFile) => {
    const ids = sel.has(r.id) && sel.size > 1 ? [...sel] : [r.id];
    setDragIds(ids); if (!sel.has(r.id)) { setSel(new Set([r.id])); setAnchor(r.id); }
  };
  const toggleSel = (id: number) => { setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); setAnchor(id); };
  const currentIds = current.map((f) => f.id);
  const allSel = currentIds.length > 0 && currentIds.every((id) => sel.has(id));
  const someSel = currentIds.some((id) => sel.has(id));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(currentIds));

  useEffect(() => {
    if (suppressLocResetRef.current) { suppressLocResetRef.current = false; return; }
    setSel(new Set()); setFilter(""); setDeepLinkMiss(false);
  }, [loc]);
  // A miss notice belongs to the visit that produced it. It used to be set and
  // never unset, so "ไม่พบเอกสารที่ลิงก์มา" followed the user around the file
  // room long after the failed link — a permanent red line about nothing.
  useEffect(() => { setDeepLinkMiss(false); }, [location.pathname]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 7000); return () => clearTimeout(t); }, [toast]);
  // Arriving from search (?file=<id>): select the document, and switch to the
  // folder that holds it so the row is actually visible in the table (not just
  // in the preview drawer) — a loose file is already in the Inbox, but a filed
  // one lives under its asset. Drop the parameter once resolved, so a refresh
  // does not re-trigger a selection the user has since moved away from.
  useEffect(() => {
    const raw = params.get("file");
    if (!raw) return;
    const id = Number(raw);
    // basePool, not `all`: `all` is fetched with the server default
    // current_only=true, so a superseded revision — which search WILL return,
    // because it filters on deleted_at only and matching on `revision` is how
    // an old one gets found — was never in the browser at all. Looking it up
    // there produced "อาจถูกลบ หรืออยู่นอกสิทธิ์ของคุณ" for a document that was
    // neither: a lie about a document the same search had just offered.
    const hit = basePool.find((f) => f.id === id);
    if (!hit) {
      if (poolLoading) return;                       // still arriving — decide later
      // Widen ONCE, through the same switch the "รวมฉบับที่ถูกแทนที่" chip
      // flips, so the list that then shows the row also SAYS superseded
      // revisions are included (§6.2). Staff only: the server hard-forces
      // current_only=true for a customer, so widening would fetch the same
      // rows again and still miss.
      if (isStaff && filters.currentOnly && widenedForRef.current !== id) {
        widenedForRef.current = id;
        setFilters((v) => ({ ...v, currentOnly: false }));
        return;                                      // leave ?file= for the re-run
      }
      // Genuinely not visible: wrong tenant, trashed, or deleted between the
      // search and the click. The message stays truthful for exactly this case.
      setDeepLinkMiss(true);
    } else {
      suppressLocResetRef.current = true;
      setLoc(hit.asset_id ? { kind: "asset", id: hit.asset_id, tag: hit.asset_tag ?? "" } : { kind: "inbox" });
      setSel(new Set([id]));
      setShowPreview(true);
      setDeepLinkMiss(false);
    }
    const next = new URLSearchParams(params);
    next.delete("file");
    setParams(next, { replace: true });
  }, [params, basePool, poolLoading, filters.currentOnly, isStaff]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Arriving from the aggregate "Documents · TK-101 · N documents" hit
  // (?asset=<id>): open that asset's folder. Without this the parameter was
  // emitted, navigated to, and ignored — the reader clicked a hit that named
  // an asset and landed in the Inbox. Same shape as ?file= above: consume,
  // then drop the parameter so a refresh does not re-navigate.
  useEffect(() => {
    const raw = params.get("asset");
    if (!raw) return;
    const id = Number(raw);
    if (!Number.isFinite(id)) return;
    if (listQ.isFetching) return;
    setLoc({ kind: "asset", id, tag: all.find((f) => f.asset_id === id)?.asset_tag ?? `#${id}` });
    const next = new URLSearchParams(params);
    next.delete("asset");
    setParams(next, { replace: true });
  }, [params, all, listQ.isFetching]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Keyboard: Esc clears · ⌘/Ctrl-A select all · Delete → Trash selected · Enter → open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "Escape") { setMenu(null); setSel(new Set()); return; }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a") { e.preventDefault(); setSel(new Set(current.map((f) => f.id))); return; }
      if (e.key === "Delete" && isStaff && sel.size && !inTrash) { e.preventDefault(); void trashFiles([...sel]); return; }
      if (e.key === "Enter" && sel.size === 1) { const f = current.find((x) => sel.has(x.id)); if (f) openFile(f); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, sel, isStaff, inTrash]);   // eslint-disable-line react-hooks/exhaustive-deps

  const crumb = loc.kind === "inbox" ? "Inbox" : loc.kind === "all" ? "All files" : loc.kind === "trash" ? "Trash"
    : loc.kind === "type" ? KIND_LABEL[loc.t] : loc.tag;
  // A bare "no files" invites the reader to conclude the archive itself is
  // empty, when it is almost always the filter row that narrowed it —
  // emptyReason names exactly what's applied (and, for a year filter, how
  // many undated documents it additionally hides IN THIS FOLDER; the
  // tenant-wide facets.undated answers a question nobody asked while standing
  // in one).
  const emptyText = emptyReason(filters, { undated: undatedInScope });
  const canFile = isStaff;
  const previewShown = showPreview && sel.size === 1;

  // Staff land on the import console (2026-08-04) — FILES' job is getting data
  // IN. The file room is one click below, unchanged, because it still does real
  // work. Customers keep the file room for now; their own send-files surface is
  // the next step, not something silently dropped.
  //
  // Exception: a search hit for a loose (unfiled) document deep-links to
  // /files/queue?file={id} — the console has no row, no selection, no preview
  // for it to land on. `cameFromFileDeepLink` routes that visit to the file
  // room instead, same as the assets lens, so the document is actually shown.
  if (isStaff && mode === "console" && !cameFromFileDeepLink) {
    return (
      <div className="absolute inset-0 flex flex-col overflow-auto" style={{ background: "var(--paper)" }}>
        <div className="px-6 h-12 border-b flex items-center shrink-0" style={{ borderColor: "var(--hairline)" }}>
          <span className="font-mono uppercase" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
            / ไฟล์
          </span>
        </div>
        <FileTabs active="queue" pendingCount={inbox.length} />

        {noTenant ? <NoTenant /> : (
        <ImportConsole
          pending={inbox}
          totals={{ all: all.length, trash: trash.length }}
          classified={classified}
          onPickFiles={() => inputRef.current?.click()}
          onImport={(f) => setImporting(f)}
          onRegisterImport={(f) => setRegisterImporting(f)}
          onFileToAsset={(f) => setPickTargets([f])}
          onOpenFileRoom={() => navigate("/files/assets")}
        />
        )}

        <input ref={inputRef} type="file" multiple className="hidden"
          onChange={(e) => { const f = Array.from(e.target.files ?? []); if (f.length) void uploadFiles(f); e.target.value = ""; }} />

        {/* The same modals the file room uses — one implementation, two entry
            points, so an import started here cannot drift from one started
            through the folder view. */}
        {pickTargets && (
          <AssetPickerModal files={pickTargets} onClose={() => setPickTargets(null)}
            onPick={async (assetId) => {
              const ids = pickTargets.map((f) => f.id); setPickTargets(null);
              await runLimited(ids, 4, async (id) => { try { await apiClient.intakeFileToAsset(id, { asset_id: assetId }); } catch { /* skip */ } });
              qc.invalidateQueries({ queryKey: ["intake"] });
            }} />
        )}
        {importing && <ImportModal file={importing} onClose={() => setImporting(null)} />}
        {registerImporting && <RegisterImportModal file={registerImporting} onClose={() => setRegisterImporting(null)} />}
        {/* Guarded, same as the file room below. Rendered unconditionally it
            parks a permanent "0 uploaded" chip over the dock — which is what
            shipped to production for one deploy. */}
        {uploads.length > 0 && (
          <UploadPanel items={uploads} open={uploadOpen} setOpen={setUploadOpen}
                       onStop={stopUploads} onRetry={retryOne} onRetryAll={retryAll}
                       onCancel={cancelOne} onClose={() => setUploads([])} />
        )}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden" style={{ background: "var(--paper)" }}
         onClick={() => setMenu(null)}>
      {/* Staff reach the other lenses through the tabs; a customer has only
          this one, so the strip would be a row of one and is left off. This
          view is also what a queue-lens deep link falls through to (see
          `cameFromFileDeepLink` above) — active reflects the real route, not
          just the room's own "assets" lens. */}
      {isStaff && <FileTabs active={lens === "assets" ? "assets" : "queue"} pendingCount={inbox.length} />}
      {/* ── Header / toolbar ── */}
      <div className="px-6 py-3 border-b flex items-center gap-4" style={{ borderColor: "var(--hairline)" }}>
        <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--accent-deep)", fontWeight: 600 }}>
          {isStaff ? "FILE ROOM" : "MY FILES"}
        </div>
        <div className="font-mono flex items-center gap-1.5" style={{ fontSize: 12, color: "var(--ink-3)" }}>
          <button onClick={() => setLoc({ kind: "inbox" })} className="hover:underline">File Room</button>
          <span style={{ color: "var(--ink-4)" }}>/</span>
          <span style={{ color: "var(--ink-1)", fontWeight: 600 }}>{crumb}</span>
        </div>
        {/* in-folder search */}
        <div className="flex items-center gap-1.5 px-2.5 py-1 ml-2" style={{ border: "1px solid var(--hairline)", background: "var(--paper-2)", minWidth: 190 }}>
          <Search size={12} style={{ color: "var(--ink-4)" }} />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search this folder…"
                 className="flex-1 outline-none bg-transparent font-mono" style={{ fontSize: 11, color: "var(--ink-1)" }} />
          {filter && <button onClick={() => setFilter("")}><X size={11} style={{ color: "var(--ink-3)" }} /></button>}
        </div>
        <div className="ml-auto flex items-center gap-2.5">
          <button onClick={() => setShowPreview((v) => !v)} className="p-1.5 border transition-colors active:translate-y-px" title="Preview pane"
                  style={{ borderColor: "var(--hairline)", background: showPreview ? "rgba(var(--accent-rgb) / 0.12)" : "var(--paper-2)", color: showPreview ? "var(--accent-deep)" : "var(--ink-3)" }}>
            <PanelRight size={14} />
          </button>
          <div className="flex items-center" style={{ border: "1px solid var(--hairline)", boxShadow: "0 1px 1px rgba(10,15,24,0.06)" }}>
            {([["details", List], ["tiles", LayoutGrid]] as const).map(([v, Icon], i) => (
              <button key={v} onClick={() => setView(v)} className="px-2.5 py-1.5 transition-colors active:translate-y-px" title={v === "details" ? "Details" : "Tiles"}
                      style={{ background: view === v ? "var(--accent)" : "var(--paper-2)", color: view === v ? "var(--ink-1)" : "var(--ink-3)", borderLeft: i > 0 ? "1px solid var(--hairline)" : "none" }}>
                <Icon size={14} />
              </button>
            ))}
          </div>
          <button onClick={() => inputRef.current?.click()}
                  className="font-mono uppercase inline-flex items-center gap-1.5 px-3.5 py-1.5 transition hover:brightness-95 active:translate-y-px"
                  style={{ fontSize: 10, letterSpacing: ".12em", color: "var(--ink-1)", background: "var(--accent)", border: "1px solid var(--accent)", fontWeight: 700, boxShadow: "0 1px 2px rgba(10,15,24,0.20)" }}>
            <UploadCloud size={13} /> Upload
          </button>
          <input ref={inputRef} type="file" multiple className="hidden"
            onChange={(e) => { const f = Array.from(e.target.files ?? []); if (f.length) void uploadFiles(f); e.target.value = ""; }} />
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 flex min-h-0 relative">
        {/* left — folders */}
        <nav className="w-[220px] shrink-0 border-r overflow-y-auto py-2" style={{ borderColor: "var(--hairline)" }}>
          <FolderRow active={loc.kind === "inbox"} dropActive={dropFolder === "inbox"} onClick={() => setLoc({ kind: "inbox" })}
                     onDragOver={canFile && dragIds.length ? (e) => { e.preventDefault(); setDropFolder("inbox"); } : undefined}
                     onDragLeave={() => setDropFolder(null)} onDrop={canFile ? () => dropOnFolder("inbox") : undefined}
                     icon={<Inbox size={15} />} label="Inbox" count={inbox.length} accentCount />
          <FolderRow active={loc.kind === "all"} onClick={() => setLoc({ kind: "all" })}
                     icon={<FileIcon size={15} />} label="All files" count={all.length} />

          {typeCounts.length > 0 && (
            <div className="px-3 pt-3 pb-1 font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".2em", color: "var(--ink-4)" }}>By type</div>
          )}
          {typeCounts.map(({ t, n }) => (
            <FolderRow key={t} active={loc.kind === "type" && loc.t === t} onClick={() => setLoc({ kind: "type", t })}
                       icon={<KindIcon kind={t} size={15} />} label={KIND_LABEL[t]} count={n} />
          ))}

          <div className="px-3 pt-3 pb-1 font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".2em", color: "var(--ink-4)" }}>Assets</div>
          {assetTree.length === 0 && (
            <div className="px-4 py-2 font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>{canFile ? "Drag files onto an asset" : "No filed documents yet"}</div>
          )}
          {assetTree.map((p) => {
            const pOpen = !collapsed.has(p.key);
            return (
              <div key={p.key}>
                <TreeHeader open={pOpen} onClick={() => toggleCollapse(p.key)} icon={<Building2 size={14} />} label={p.name} count={p.count} depth={0} />
                {pOpen && p.areas.map((a) => {
                  const aOpen = !collapsed.has(a.key);
                  const single = p.areas.length === 1 && a.name === "—";   // hide a lone unnamed area
                  return (
                    <div key={a.key}>
                      {!single && <TreeHeader open={aOpen} onClick={() => toggleCollapse(a.key)} icon={<Layers size={13} />} label={a.name} depth={1} />}
                      {(aOpen || single) && a.assets.map((f) => (
                        <FolderRow key={f.id} active={loc.kind === "asset" && loc.id === f.id} dropActive={dropFolder === f.id} depth={single ? 1 : 2}
                                   onClick={() => setLoc({ kind: "asset", id: f.id, tag: f.tag })}
                                   onDragOver={canFile && dragIds.length ? (e) => { e.preventDefault(); setDropFolder(f.id); } : undefined}
                                   onDragLeave={() => setDropFolder(null)} onDrop={canFile ? () => dropOnFolder(f.id) : undefined}
                                   icon={<Folder size={14} />} label={f.tag} count={f.count} />
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {isStaff && trash.length > 0 && (
            <>
              <div className="px-3 pt-3 pb-1 font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".2em", color: "var(--ink-4)" }}>System</div>
              <FolderRow active={loc.kind === "trash"} onClick={() => setLoc({ kind: "trash" })}
                         icon={<Trash2 size={15} />} label="Trash" count={trash.length} />
            </>
          )}
        </nav>

        {/* middle — files (right padding opens room for the preview drawer) */}
        <div className="flex-1 flex flex-col min-w-0 relative"
             style={{ paddingRight: previewShown ? 300 : 0, transition: "padding .22s ease" }}
             onDragOver={(e) => { if (isFileDrag(e)) { e.preventDefault(); setDragging(true); } }}
             onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }} onDrop={onPaneDrop}>
          {/* status / bulk bar */}
          <div className="px-6 py-2 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--hairline)" }}>
            <span className="font-mono flex items-center gap-2" style={{ fontSize: 10.5, color: "var(--ink-4)", letterSpacing: ".04em" }}>
              <span>{current.length} item{current.length === 1 ? "" : "s"}{sel.size > 0 ? ` · ${sel.size} selected` : ""}</span>
              {loc.kind === "asset" && (
                <button onClick={() => navigate(`/assets/${loc.id}`)} className="uppercase inline-flex items-center gap-1 px-2 py-0.5 border transition hover:bg-[rgba(var(--accent-rgb)_/_0.08)] active:translate-y-px"
                        style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--accent-deep)", borderColor: "var(--accent)", fontWeight: 700 }}>Open asset →</button>
              )}
              {canFile && sel.size > 0 && !previewShown && !inTrash && (
                <>
                  <button onClick={() => setPickTargets(selectedFiles)} className="uppercase inline-flex items-center gap-1 px-2 py-0.5 border transition hover:bg-[rgba(var(--accent-rgb)_/_0.08)] active:translate-y-px"
                          style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--accent-deep)", borderColor: "var(--accent)", fontWeight: 700 }}>
                    <FolderInput size={10} /> File {sel.size} to asset
                  </button>
                  {/* The bulk half of §4.4. Historical documents arrive as a
                      pile; without this the year filter has nothing to filter
                      on, because nobody dates 400 documents one at a time. */}
                  <button data-testid="bulk-set-date" onClick={() => void setDatesFor([...sel])} className="uppercase inline-flex items-center gap-1 px-2 py-0.5 border transition hover:bg-[rgba(var(--accent-rgb)_/_0.08)] active:translate-y-px"
                          style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--accent-deep)", borderColor: "var(--accent)", fontWeight: 700 }}>
                    <CalendarDays size={10} /> ระบุวันที่ {sel.size} ฉบับ
                  </button>
                  <button onClick={() => void trashFiles([...sel])} className="uppercase inline-flex items-center gap-1 px-2 py-0.5 border transition hover:bg-[rgba(192,57,43,0.08)] active:translate-y-px"
                          style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--err)", borderColor: "var(--err)", fontWeight: 700 }}>
                    <Trash2 size={10} /> Delete
                  </button>
                </>
              )}
              {isStaff && sel.size > 0 && !previewShown && !inTrash && (
                <button onClick={() => void recordAsDelivery([...sel])} className="uppercase inline-flex items-center gap-1 px-2 py-0.5 transition hover:brightness-95 active:translate-y-px"
                        style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--ink-1)", background: "var(--accent)", border: "1px solid var(--accent)", fontWeight: 700 }}>
                  <UploadCloud size={10} /> บันทึกเป็นรอบส่ง ({sel.size})
                </button>
              )}
              {canFile && sel.size > 0 && inTrash && (
                <>
                  <button onClick={() => void restoreFiles([...sel])} className="uppercase inline-flex items-center gap-1 px-2 py-0.5 border transition hover:bg-[rgba(var(--accent-rgb)_/_0.08)] active:translate-y-px"
                          style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--accent-deep)", borderColor: "var(--accent)", fontWeight: 700 }}>
                    <RotateCw size={10} /> Restore {sel.size}
                  </button>
                  <button onClick={() => void purgeFiles([...sel])} className="uppercase inline-flex items-center gap-1 px-2 py-0.5 border transition hover:bg-[rgba(192,57,43,0.08)] active:translate-y-px"
                          style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--err)", borderColor: "var(--err)", fontWeight: 700 }}>
                    <Trash2 size={10} /> Delete forever
                  </button>
                </>
              )}
              {canFile && dragIds.length > 0 && <span style={{ color: "var(--accent-deep)" }}>drag onto a folder to file {dragIds.length > 1 ? `${dragIds.length} files` : "it"} →</span>}
            </span>
          </div>

          {deepLinkMiss && (
            <div className="px-4 py-2 font-mono" style={{ fontSize: 11.5, color: "var(--err)" }}>
              ไม่พบเอกสารที่ลิงก์มา — อาจถูกลบ หรืออยู่นอกสิทธิ์ของคุณ
            </div>
          )}

          <DocumentFilters value={filters} facets={facets} onChange={setFilters} isStaff={isStaff} />

          <div className="flex-1 overflow-auto">
            {view === "details"
              ? <DetailsView files={current} pending={listQ.isPending} emptyText={emptyText} selected={(id) => sel.has(id)}
                             sort={sort} setSort={setSort} onClick={rowClick} onMenu={rowMenu}
                             onToggle={toggleSel} allSel={allSel} someSel={someSel} onToggleAll={toggleAll}
                             onDragStart={canFile && !inTrash ? rowDragStart : undefined} onDragEnd={() => { setDragIds([]); setDropFolder(null); }} />
              : <TilesView files={current} pending={listQ.isPending} emptyText={emptyText} selected={(id) => sel.has(id)}
                           onClick={rowClick} onMenu={rowMenu}
                           onDragStart={canFile && !inTrash ? rowDragStart : undefined} onDragEnd={() => { setDragIds([]); setDropFolder(null); }} />}
          </div>

          {dragging && (
            <div className="absolute inset-0 z-[60] flex items-center justify-center pointer-events-none" style={{ background: "rgba(var(--accent-rgb) / 0.08)", border: "2px dashed var(--accent)" }}>
              <div className="font-mono uppercase flex items-center gap-2" style={{ fontSize: 13, letterSpacing: ".12em", color: "var(--accent-deep)" }}>
                <UploadCloud size={20} /> Drop to upload to Inbox
              </div>
            </div>
          )}
        </div>

        {/* right — preview drawer (slides in when exactly one file is selected) */}
        <PreviewPane file={previewFile} show={previewShown} isStaff={isStaff} inTrash={inTrash} navigate={navigate}
                     onClose={() => setSel(new Set())} onFile={(f) => setPickTargets([f])}
                     onRename={renameOne} onEditNote={editNote} onEditDate={editDate} onImport={(f) => setImporting(f)}
                     onImportRegister={(f) => setRegisterImporting(f)}
                     onDelete={(f) => void trashFiles([f.id])}
                     onRestore={(f) => void restoreFiles([f.id])} onPurge={(f) => void purgeFiles([f.id])} />
      </div>

      {/* footer */}
      <div className="px-6 py-1.5 border-t font-mono" style={{ borderColor: "var(--hairline)", fontSize: 9, color: "var(--ink-4)", letterSpacing: ".04em" }}>
        Files are isolated per tenant · max {MAX_MB} MB / file · visible only to your organization and the XZYNEXA team on your account
      </div>

      {uploads.length > 0 && (
        <UploadPanel items={uploads} open={uploadOpen} setOpen={setUploadOpen}
          onStop={stopUploads} onRetry={retryOne} onRetryAll={retryAll} onCancel={cancelOne} onClose={() => setUploads([])} />
      )}

      {menu && (
        <ContextMenu menu={menu} isStaff={isStaff} inTrash={inTrash} navigate={navigate}
          onOpen={() => { openFile(menu.file); setMenu(null); }}
          onFile={() => { setPickTargets(sel.has(menu.file.id) && sel.size > 1 ? selectedFiles : [menu.file]); setMenu(null); }}
          onUnfile={() => { route.mutate({ id: menu.file.id, asset_id: null }); setMenu(null); }}
          onRename={() => { renameOne(menu.file); setMenu(null); }}
          onEditNote={() => { editNote(menu.file); setMenu(null); }}
          onEditDate={() => {
            const ids = sel.has(menu.file.id) && sel.size > 1 ? [...sel] : null;
            setMenu(null);
            if (ids) void setDatesFor(ids); else editDate(menu.file);
          }}
          canImport={!inTrash && canImportFile(menu.file)}
          onImport={() => { setImporting(menu.file); setMenu(null); }}
          canImportRegister={!inTrash && canRegisterImportFile(menu.file)}
          onImportRegister={() => { setRegisterImporting(menu.file); setMenu(null); }}
          onDelete={() => { const ids = sel.has(menu.file.id) && sel.size > 1 ? [...sel] : [menu.file.id]; void trashFiles(ids); setMenu(null); }}
          onRestore={() => { const ids = sel.has(menu.file.id) && sel.size > 1 ? [...sel] : [menu.file.id]; void restoreFiles(ids); setMenu(null); }}
          onPurge={() => { const ids = sel.has(menu.file.id) && sel.size > 1 ? [...sel] : [menu.file.id]; void purgeFiles(ids); setMenu(null); }}
          selCount={sel.has(menu.file.id) ? sel.size : 1} />
      )}

      {pickTargets && (
        <AssetPickerModal files={pickTargets} onClose={() => setPickTargets(null)}
          onPick={async (assetId) => {
            const ids = pickTargets.map((f) => f.id); setPickTargets(null);
            await runLimited(ids, 4, async (id) => { try { await apiClient.intakeFileToAsset(id, { asset_id: assetId }); } catch { /* skip */ } });
            setSel(new Set()); qc.invalidateQueries({ queryKey: ["intake"] });
          }} />
      )}

      {importing && <ImportModal file={importing} onClose={() => setImporting(null)} />}

      {registerImporting && <RegisterImportModal file={registerImporting} onClose={() => setRegisterImporting(null)} />}

      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 z-[1250] flex items-center gap-3 px-4 py-2.5 shadow-2xl"
             style={{ bottom: 40, background: "var(--ink-1)", border: "1px solid var(--hairline)" }}>
          <span className="font-mono" style={{ fontSize: 12, color: "#fff" }}>{toast.msg}</span>
          {toast.undo && (
            <button onClick={() => void toast.undo!()} className="font-mono uppercase px-2 py-0.5 border transition hover:bg-[rgba(var(--accent-rgb)_/_0.15)]"
                    style={{ fontSize: 9.5, letterSpacing: ".1em", color: "var(--accent-2)", borderColor: "var(--accent-2)", fontWeight: 700 }}>Undo</button>
          )}
          <button onClick={() => setToast(null)} style={{ color: "var(--ink-4)" }}><X size={13} /></button>
        </div>
      )}
    </div>
  );
}


// Moved verbatim from DeskHomeScreen when "/" retired (2026-09-01). It is the
// one part of that page with nowhere else to go: the invitation to import must
// be withheld while no tenant is bound, or the upload lands on the DEFAULT
// sentinel. It says which choice is missing — never "ไม่มีข้อมูล", which would
// be a claim about the data rather than about the reader's own selection.
function NoTenant(): JSX.Element {
  return (
    <div data-testid="state-no-tenant" className="p-6" style={{ maxWidth: 860, margin: "0 auto", width: "100%" }}>
      <h1 className="font-display font-medium m-0"
          style={{ fontSize: 22, color: "var(--ink-1)", letterSpacing: "-.02em" }}>
        เลือกหน่วยงานก่อน
      </h1>
      <p className="font-sans m-0"
         style={{ marginTop: 8, fontSize: 13.5, lineHeight: 1.6, color: "var(--ink-3)" }}>
        เลือกหน่วยงานที่แถบสถานะด้านล่าง แล้วห้องไฟล์กับการนำเข้าข้อมูลจะขึ้นมา —
        ตอนนี้ยังไม่ได้เลือก จึงยังนำข้อมูลเข้าไม่ได้
      </p>
    </div>
  );
}
